import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, MapPin } from 'lucide-react';
import { ValidationBadge } from '../ValidationBadge';
import { coordLabel, mapPath, monsterPath, spotPath } from '../../../utils/worldFormat';
import type { QuestDropView } from '../../../utils/questView';
import { EntityThumb } from '../../world/EntityThumb';
const SHOW = 6;

/**
 * Drop de misión: requiere la misión aceptada, clase elegible e inventario sin el objeto.
 * La tirada es condicional; no es una probabilidad final por muerte ni un drop general.
 */
export function QuestDropPanel({
  drop
}: {
  drop: QuestDropView;
}) {
  const shown = drop.targets.slice(0, SHOW);
  const rest = drop.targets.slice(SHOW);
  return <div className="mt-2 rounded-xl border border-emerald-300/20 bg-emerald-300/[0.04] p-3" data-id="element-994" data-mp="1crzfcn0jve6n">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white" data-id="element-995" data-mp="6e03fbaiqstx">
        <ScrollText className="h-4 w-4 text-emerald-200" aria-hidden="true" data-id="element-996" data-mp="1dkrmo5vxte2" />
        Se obtiene durante la misión
        <ValidationBadge status="documentado" label="Confirmado por configuración" data-id="element-997" data-mp="20swbzwho0fgn" />
      </p>
      <ul className="mt-1.5 space-y-0.5 text-xs text-amber-100" data-id="element-998" data-mp="25pwgi2yv9a5n">
        {drop.conditions.map(c => <li key={c} data-id="element-999" data-mp="1qughdjjfio2e">· {c}</li>)}
      </ul>
      {drop.roll && <p className="mt-1.5 text-xs text-gray-300" data-id="element-1000" data-mp="1vsrqtt5tawk3">
          Tirada de misión: <span className="font-semibold text-white" data-id="element-1001" data-mp="1vqx8mmlxdijp">{drop.roll}</span> por intento válido. No es la probabilidad final por muerte.
        </p>}
      {drop.targets.length > 0 ? <>
          <p className="mt-2 text-xs text-gray-400" data-id="element-1002" data-mp="1mtei52piic0f">{drop.targets.length} {drop.targets.length === 1 ? 'monstruo' : 'combinaciones de monstruo y mapa'}. Posibles, no garantizadas.</p>
          <ul className="mt-1.5 grid gap-1.5 sm:grid-cols-2" data-id="element-1003" data-mp="1d2s77lsqq9o2">{shown.map(t => <TargetRow key={`${t.monsterId}-${t.mapId ?? ''}`} t={t} data-id="element-1004" data-mp="z291wmfnthpl" />)}</ul>
          {rest.length > 0 && <details className="mt-1.5" data-id="element-1005" data-mp="1331bjgsg1ome">
              <summary className="cursor-pointer text-xs font-semibold text-[#D4AF37]" data-id="element-1006" data-mp="136708ux5uohl">Ver {rest.length} más</summary>
              <ul className="mt-1.5 grid gap-1.5 sm:grid-cols-2" data-id="element-1007" data-mp="3101e6jcmdlx7">{rest.map(t => <TargetRow key={`${t.monsterId}-${t.mapId ?? ''}`} t={t} data-id="element-1008" data-mp="30yqaskn36g6m" />)}</ul>
            </details>}
        </> : <p className="mt-1.5 text-xs text-gray-400" data-id="element-1009" data-mp="2r1vg6ctxozxm">Monstruos de esta regla pendientes de documentar.</p>}
    </div>;
}
function TargetRow({
  t
}: {
  t: QuestDropView['targets'][number];
}) {
  const spot = t.spawns[0];
  const coord = spot ? coordLabel(spot) : undefined;
  return <li className="flex gap-2.5 rounded-lg border border-white/10 bg-[#0A1628] px-2.5 py-2 text-xs" data-id="element-1010" data-mp="3jopxj0vgfj6v">
      <EntityThumb entity={{
      type: 'monster',
      id: t.monsterId
    }} name={t.monsterName} data-id="element-1011" data-mp="3etalmeh0bxd3" />
      <span className="min-w-0 flex-1" data-id="element-1012" data-mp="3arxcnvimbe1y">
      <Link to={monsterPath(t.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1013" data-mp="23csosd9hzspx">{t.monsterName}</Link>
      <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-gray-400" data-id="element-1014" data-mp="esn53idwbtbi">
        <MapPin className="h-3 w-3 text-[#D4AF37]" aria-hidden="true" data-id="element-1015" data-mp="eqk7ac6i4vw8" />
        {t.mapId !== undefined ? <Link to={mapPath(t.mapId)} className="hover:text-white" data-id="element-1016" data-mp="irw0erol3z96">{t.mapName ?? `Mapa ${t.mapId}`}</Link> : <span data-id="element-1017" data-mp="sjrzo62epvez">Mapa no indicado</span>}
        {coord ? <Link to={spotPath(spot.id)} className="font-mono text-[11px] text-gray-300 hover:text-white" data-id="element-1018" data-mp="sm4kcqzjujvq">· {coord}{t.spawns.length > 1 ? ` (+${t.spawns.length - 1})` : ''}</Link> : <span className="text-gray-500" data-id="element-1019" data-mp="12kc23c1nx5ys">· ubicación exacta no documentada</span>}
      </span>
      </span>
    </li>;
}